import api from './api.service';

export interface PendingApprovalPRRow {
  id: string;
  pr_number: string;
  pr_date: string;
  required_date: string;
  estimated_total: number;
}

export interface PendingApprovalBidAnalysisRow {
  id: string;
  analysis_number: string;
  created_at: string;
}

export interface PendingApprovalPORow {
  id: string;
  po_number: string;
  po_date: string;
  delivery_date: string;
  grand_total: number;
  supplier_name: string;
}

export interface PendingApprovalGrnRow {
  id: string;
  grn_number: string;
  grn_date: string;
  supplier_id: string;
}

export interface PendingApprovalReturnRow {
  id: string;
  return_number: string;
  return_date: string;
}

export type PoAgingBucket = '0-7' | '8-14' | '15-30' | '30+';

export interface OverdueOrDueSoonPORow {
  id: string;
  po_number: string;
  po_date: string;
  delivery_date: string;
  grand_total: number;
  supplier_name: string;
  supplier_id?: string | null;
  po_status?: string;
  buyer?: string | null;
  overdue_days?: number;
  aging_bucket?: PoAgingBucket;
  link_path?: string;
  supplier_link_path?: string | null;
}

export interface PRPendingSourcingRow {
  id: string;
  pr_number: string;
  required_date: string;
  estimated_total: number;
}

export interface GrnNotInvoicedRow {
  id: string;
  grn_number: string;
  grn_date: string;
  supplier_id: string;
  supplier_name?: string;
}

export interface SupplyChainDashboardActionSummary {
  pending_approval_prs: PendingApprovalPRRow[];
  pending_approval_bid_analyses: PendingApprovalBidAnalysisRow[];
  pending_approval_pos: PendingApprovalPORow[];
  pending_approval_grns: PendingApprovalGrnRow[];
  pending_approval_returns: PendingApprovalReturnRow[];
  overdue_pos: OverdueOrDueSoonPORow[];
  overdue_po_count: number;
  overdue_po_value: number;
  overdue_aging: Record<PoAgingBucket, number>;
  due_soon_pos: OverdueOrDueSoonPORow[];
  due_soon_po_count: number;
  due_soon_po_value: number;
  prs_pending_sourcing: PRPendingSourcingRow[];
  grn_not_invoiced: GrnNotInvoicedRow[];
  draft_pr_count: number;
  draft_po_count: number;
  draft_grn_count: number;
  pos_requiring_advance_count?: number;
  pending_supplier_advance_requests_count?: number;
}

export interface DelayedPoQueueResult {
  mode: 'overdue' | 'due_soon';
  total: number;
  page: number;
  pageSize: number;
  total_value: number;
  aging: Record<PoAgingBucket, number>;
  rows: OverdueOrDueSoonPORow[];
}

function emptyAging(): Record<PoAgingBucket, number> {
  return { '0-7': 0, '8-14': 0, '15-30': 0, '30+': 0 };
}

export function emptySupplyChainDashboardSummary(): SupplyChainDashboardActionSummary {
  return {
    pending_approval_prs: [],
    pending_approval_bid_analyses: [],
    pending_approval_pos: [],
    pending_approval_grns: [],
    pending_approval_returns: [],
    overdue_pos: [],
    overdue_po_count: 0,
    overdue_po_value: 0,
    overdue_aging: emptyAging(),
    due_soon_pos: [],
    due_soon_po_count: 0,
    due_soon_po_value: 0,
    prs_pending_sourcing: [],
    grn_not_invoiced: [],
    draft_pr_count: 0,
    draft_po_count: 0,
    draft_grn_count: 0,
  };
}

function emptySummary(): SupplyChainDashboardActionSummary {
  return emptySupplyChainDashboardSummary();
}

export async function getSupplyChainDashboardActionSummary(params?: {
  as_on_date?: string;
}): Promise<SupplyChainDashboardActionSummary> {
  const res = await api.get<{ data?: SupplyChainDashboardActionSummary }>('/procurement/dashboard/action-summary', {
    params: params?.as_on_date ? { as_on_date: params.as_on_date } : undefined,
  });
  const data = (res as any)?.data?.data ?? (res as any)?.data ?? res;
  if (!data || typeof data !== 'object') {
    return emptySummary();
  }
  const aging = data.overdue_aging ?? emptyAging();
  return {
    pending_approval_prs: Array.isArray(data.pending_approval_prs) ? data.pending_approval_prs : [],
    pending_approval_bid_analyses: Array.isArray(data.pending_approval_bid_analyses) ? data.pending_approval_bid_analyses : [],
    pending_approval_pos: Array.isArray(data.pending_approval_pos) ? data.pending_approval_pos : [],
    pending_approval_grns: Array.isArray(data.pending_approval_grns) ? data.pending_approval_grns : [],
    pending_approval_returns: Array.isArray(data.pending_approval_returns) ? data.pending_approval_returns : [],
    overdue_pos: Array.isArray(data.overdue_pos) ? data.overdue_pos : [],
    overdue_po_count: Number(data.overdue_po_count ?? data.overdue_pos?.length ?? 0),
    overdue_po_value: Number(data.overdue_po_value ?? 0),
    overdue_aging: {
      '0-7': Number(aging['0-7'] ?? 0),
      '8-14': Number(aging['8-14'] ?? 0),
      '15-30': Number(aging['15-30'] ?? 0),
      '30+': Number(aging['30+'] ?? 0),
    },
    due_soon_pos: Array.isArray(data.due_soon_pos) ? data.due_soon_pos : [],
    due_soon_po_count: Number(data.due_soon_po_count ?? data.due_soon_pos?.length ?? 0),
    due_soon_po_value: Number(data.due_soon_po_value ?? 0),
    prs_pending_sourcing: Array.isArray(data.prs_pending_sourcing) ? data.prs_pending_sourcing : [],
    grn_not_invoiced: Array.isArray(data.grn_not_invoiced) ? data.grn_not_invoiced : [],
    draft_pr_count: Number(data.draft_pr_count ?? 0),
    draft_po_count: Number(data.draft_po_count ?? 0),
    draft_grn_count: Number(data.draft_grn_count ?? 0),
    pos_requiring_advance_count: Number(data.pos_requiring_advance_count ?? 0),
    pending_supplier_advance_requests_count: Number(data.pending_supplier_advance_requests_count ?? 0),
  };
}

export async function getSupplyChainDelayedPoQueue(params?: {
  mode?: 'overdue' | 'due_soon';
  page?: number;
  pageSize?: number;
  aging_bucket?: PoAgingBucket;
  search?: string;
  as_on_date?: string;
  due_soon_days?: number;
}): Promise<DelayedPoQueueResult> {
  const res = await api.get<{ data?: DelayedPoQueueResult }>('/procurement/dashboard/delayed-po-queue', {
    params,
  });
  const data = (res as any)?.data?.data ?? (res as any)?.data ?? res;
  return {
    mode: data?.mode === 'due_soon' ? 'due_soon' : 'overdue',
    total: Number(data?.total ?? 0),
    page: Number(data?.page ?? 1),
    pageSize: Number(data?.pageSize ?? 20),
    total_value: Number(data?.total_value ?? 0),
    aging: {
      '0-7': Number(data?.aging?.['0-7'] ?? 0),
      '8-14': Number(data?.aging?.['8-14'] ?? 0),
      '15-30': Number(data?.aging?.['15-30'] ?? 0),
      '30+': Number(data?.aging?.['30+'] ?? 0),
    },
    rows: Array.isArray(data?.rows) ? data.rows : [],
  };
}
